import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider, FlowingWaves, TattooPathDrawing } from '@/components/feature/DrawingLines';

const seniorArtists = [
  {
    name: '김선율',
    role: 'Head Artist / Founder',
    specialty: '파인라인 · 미니멀리즘',
    career: '경력 8년',
    desc: '선율의 창립자. 극세선 라인워크의 선구자로 국내 타투 씬에서 독보적인 위치를 차지하고 있습니다.',
    tags: ['#파인라인', '#미니멀', '#블랙앤그레이'],
    src: 'https://public.readdy.ai/ai/img_res/954c674ebc30f78d6021da688691a1d8.jpg',
  },
  {
    name: '이하린',
    role: 'Senior Artist',
    specialty: '보태니컬 · 수채화',
    career: '경력 6년',
    desc: '자연에서 영감을 받은 보태니컬 디자인의 전문가. 섬세한 수채화 기법으로 피부 위에 정원을 그립니다.',
    tags: ['#보태니컬', '#수채화', '#컬러'],
    src: 'https://readdy.ai/api/search-image?query=professional%20Korean%20female%20tattoo%20artist%20portrait%20white%20minimalist%20studio%20background%20artistic%20confident%20expression%20black%20outfit%20editorial%20photography%20natural%20lighting&width=400&height=500&seq=artist_senior2&orientation=portrait',
  },
  {
    name: '박준서',
    role: 'Senior Artist',
    specialty: '이레즈미 · 올드스쿨',
    career: '경력 7년',
    desc: '전통 이레즈미와 현대적 감각을 결합한 독창적인 스타일. 대형 작업의 완성도로 정평이 나 있습니다.',
    tags: ['#이레즈미', '#올드스쿨', '#대형작업'],
    src: 'https://readdy.ai/api/search-image?query=professional%20Korean%20male%20tattoo%20artist%20portrait%20white%20studio%20background%20serious%20artistic%20expression%20black%20outfit%20minimalist%20aesthetic%20editorial%20photography&width=400&height=500&seq=artist_senior3&orientation=portrait',
  },
];

const juniorArtists = [
  {
    name: '최아름',
    role: 'Junior Artist',
    specialty: '레터링 · 미니타투',
    career: '경력 2년',
    src: 'https://readdy.ai/api/search-image?query=young%20Korean%20female%20tattoo%20artist%20portrait%20clean%20white%20background%20professional%20smile%20warm%20expression%20black%20outfit%20editorial%20photography&width=400&height=500&seq=artist_junior1&orientation=portrait',
  },
  {
    name: '정민준',
    role: 'Junior Artist',
    specialty: '기하학 · 도형',
    career: '경력 1.5년',
    src: 'https://readdy.ai/api/search-image?query=young%20Korean%20male%20tattoo%20artist%20portrait%20clean%20white%20background%20professional%20confident%20expression%20black%20outfit%20editorial%20photography&width=400&height=500&seq=artist_junior2&orientation=portrait',
  },
  {
    name: '한소희',
    role: 'Junior Artist',
    specialty: '감성 일러스트',
    career: '경력 2년',
    src: 'https://readdy.ai/api/search-image?query=young%20Korean%20female%20tattoo%20artist%20portrait%20clean%20white%20background%20professional%20artistic%20expression%20black%20outfit%20editorial%20photography%20natural%20light&width=400&height=500&seq=artist_junior3&orientation=portrait',
  },
  {
    name: '윤태양',
    role: 'Junior Artist',
    specialty: '블랙워크 · 도트',
    career: '경력 1년',
    src: 'https://readdy.ai/api/search-image?query=young%20Korean%20male%20tattoo%20artist%20portrait%20clean%20white%20background%20professional%20calm%20expression%20black%20outfit%20editorial%20photography%20studio%20light&width=400&height=500&seq=artist_junior4&orientation=portrait',
  },
];

const academySteps = [
  { step: '01', title: '기초 이론 교육', period: '2주', desc: '타투 역사, 피부 구조, 위생 이론, 색채학 등 기초 지식 습득' },
  { step: '02', title: '기술 실습', period: '6주', desc: '라인워크, 쉐이딩, 컬러링 등 핵심 기술 집중 훈련' },
  { step: '03', title: '위생 인증', period: '1주', desc: 'Medical Grade 위생 프로토콜 교육 및 인증 시험 통과' },
  { step: '04', title: '현장 실습', period: '4주', desc: '시니어 아티스트 지도 하에 실제 고객 시술 경험' },
  { step: '05', title: '최종 심사', period: '1주', desc: '포트폴리오 심사 및 기술 평가 통과 시 선율 아티스트 인증' },
];

function ArtistsHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <section className="relative pt-40 pb-24 bg-[#1A1A1A] overflow-hidden">
      <div className="absolute inset-0 opacity-50">
        <img
          src="https://readdy.ai/api/search-image?query=skilled%20Korean%20tattoo%20artist%20hands%20close%20up%20drawing%20intricate%20fine%20line%20botanical%20tattoo%20on%20skin%20professional%20studio%20warm%20focused%20lighting%20macro%20detail%20shot%20premium%20artistic%20editorial%20photography%20black%20gloves%20needle&width=1440&height=600&seq=artists_hero_v2&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-transparent to-[#1A1A1A]/60" />
      <FlowingWaves color="rgba(201,169,110,0.1)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.2)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />
      <div className="relative z-10 max-w-[1440px] mx-auto px-8 md:px-16 text-center">
        <div className="flex items-center justify-center gap-2 mb-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}>
          <Link to="/brand" className="text-[10px] tracking-[0.3em] uppercase text-white/30 hover:text-white/60 transition-colors cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>Brand</Link>
          <span className="text-white/20 text-xs">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]" style={{ fontFamily: "'Montserrat', sans-serif" }}>아티스트 크루</span>
        </div>
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out 0.15s' }}>
          <h1 className="text-4xl md:text-6xl text-white mb-6 leading-tight" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.1em' }}>
            아티스트 크루
          </h1>
          <p className="text-sm md:text-base text-white/50 leading-relaxed max-w-lg mx-auto" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.8' }}>
            선율의 아티스트는 단순한 시술자가 아닙니다.<br />
            고객의 이야기를 피부 위에 새기는 예술가입니다.
          </p>
        </div>
        <div className="mx-auto mt-12 h-px bg-white/15 transition-all duration-1000 ease-out" style={{ width: visible ? '80px' : '0px', transitionDelay: '500ms' }} />
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-10 md:gap-20"
          style={{ opacity: visible ? 1 : 0, transition: 'all 1s ease-out 0.6s' }}>
          {[{ value: '7명+', label: '소속 아티스트' }, { value: '6개월', label: '아카데미 과정' }, { value: '100%', label: '본사 인증 아티스트' }].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-2xl md:text-3xl text-white mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }}>{s.value}</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/30" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SeniorArtistsSection() {
  const { ref, visible } = useScrollAnimation(0.05);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <section className="py-28 md:py-36 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-left" color="rgba(201,169,110,0.1)" size={160} />
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.08)" size={140} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="text-center mb-10" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}>
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Senior Artists</p>
          <h2 className="text-3xl md:text-4xl text-[#222222]" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>시니어 아티스트</h2>
        </div>
        <div className="mb-12">
          <GoldLineDivider delay={0.2} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {seniorArtists.map((artist, i) => (
            <div key={artist.name}
              className="group cursor-default"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(50px)', transition: 'opacity 0.7s ease-out, transform 0.7s ease-out', transitionDelay: `${i * 130 + 200}ms` }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              <div className="relative overflow-hidden mb-6" style={{ height: '380px' }}>
                <img src={artist.src} alt={artist.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex flex-wrap gap-1.5 overflow-hidden transition-all duration-300"
                    style={{ maxHeight: hoveredIdx === i ? '60px' : '0px', opacity: hoveredIdx === i ? 1 : 0 }}>
                    {artist.tags.map((tag) => (
                      <span key={tag} className="text-[10px] px-2.5 py-1 rounded-full border border-[#C9A96E]/60 text-[#C9A96E] whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#C9A96E] mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>{artist.role}</p>
                    <h3 className="text-xl text-[#222222]" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>{artist.name}</h3>
                  </div>
                  <span className="text-[10px] px-2.5 py-1 bg-[#F5F5F5] text-[#888] rounded-full whitespace-nowrap mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>{artist.career}</span>
                </div>
                <p className="text-xs text-[#AAAAAA] mb-2 font-medium" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{artist.specialty}</p>
                <p className="text-xs text-[#888] leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>{artist.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function JuniorArtistsSection() {
  const { ref, visible } = useScrollAnimation(0.05);

  return (
    <section className="py-28 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.1)" size={150} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="text-center mb-10" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}>
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Junior Artists</p>
          <h2 className="text-3xl md:text-4xl text-[#222222]" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>주니어 아티스트</h2>
        </div>
        <div className="mb-12">
          <GoldLineDivider delay={0.2} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {juniorArtists.map((artist, i) => (
            <div key={artist.name} className="group cursor-default"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease-out, transform 0.7s ease-out', transitionDelay: `${i * 100 + 200}ms` }}>
              <div className="relative overflow-hidden mb-4" style={{ height: '260px' }}>
                <img src={artist.src} alt={artist.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/50 via-transparent to-transparent" />
              </div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#C9A96E] mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>{artist.role}</p>
              <h3 className="text-base text-[#222222] mb-1" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.06em' }}>{artist.name}</h3>
              <p className="text-xs text-[#AAAAAA]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{artist.specialty} · {artist.career}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcademySection() {
  const { ref, visible } = useScrollAnimation(0.05);

  return (
    <section className="py-28 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.1)" size={160} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 1s ease-out' }}>
            <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Academy</p>
            <h2 className="text-3xl md:text-4xl text-[#222222] mb-6 leading-tight" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>
              선율 아카데미<br /><span className="text-[#AAAAAA]">6개월 집중 과정</span>
            </h2>
            <p className="text-sm text-[#888] leading-relaxed mb-8" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
              선율 아카데미는 단순한 기술 교육을 넘어, 예술가로서의 철학과 의료 수준의 위생 의식을 함께 키웁니다. 수료 후 선율 인증 아티스트로 활동할 수 있습니다.
            </p>
            {/* Tattoo path drawing accent */}
            <div className="mb-8" style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease 0.5s' }}>
              <TattooPathDrawing color="rgba(201,169,110,0.3)" width={160} height={110} />
            </div>
            <Link to="/community/inquiry" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#222222] text-white text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:bg-[#001220] transition-colors duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              아카데미 문의하기
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="flex flex-col gap-0" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(40px)', transition: 'all 1s ease-out 0.2s' }}>
            {academySteps.map((s, i) => (
              <div key={s.step} className="flex gap-5 py-6 border-b border-[#F5F5F5] last:border-0"
                style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.6s ease-out', transitionDelay: `${i * 80 + 400}ms` }}>
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-[#EBEBEB]">
                  <span className="text-[11px] font-semibold text-[#C9A96E]" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.step}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1.5">
                    <h3 className="text-sm font-semibold text-[#222222]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{s.title}</h3>
                    <span className="text-[10px] px-2 py-0.5 bg-[#F5F5F5] text-[#888] rounded-full whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.period}</span>
                  </div>
                  <p className="text-xs text-[#888] leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function BrandArtistsPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <ArtistsHero />
        <SeniorArtistsSection />
        <JuniorArtistsSection />
        <AcademySection />
      </main>
      <Footer />
    </div>
  );
}
